// "Detected on your PCs, not in your library" (F-CAT-03, spec/13 §9.2): titles the venue's stations reported
// installed that no library entry matches — Steam app manifests, Epic installs, and Ubisoft / EA / Battle.net
// uninstall entries — grouped by title with how many PCs have it. A title the platform catalogue holds (a Steam game
// by the app id its launch recipe runs, else by its exact name) is added in one click through the ordinary
// `addLibraryEntry` (`library:manage`); one the catalogue does not hold becomes a **request to HQ** in one click
// (F-CAT-08, `createCatalogueRequest`) — HQ adds it to the catalogue, and it can then be added here.

import type { DetectedTitles as DetectedTitlesDto } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { Section } from '../../ui';
import { useAddLibraryEntry, useCreateCatalogueRequest, useDetectedTitles } from './queries';
import { useProblem } from '../../api/use-problem';

type Detected = DetectedTitlesDto['titles'][number];

/** Launcher names are the launchers' own words (catalogue data, not copy). */
const SOURCE_NAMES: Readonly<Record<Detected['source'], string>> = {
  steam: 'Steam',
  epic: 'Epic Games',
  ubisoft: 'Ubisoft Connect',
  ea: 'EA app',
  battlenet: 'Battle.net',
};

export function DetectedTitles({
  organizationId,
  venueId,
  canManage,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly canManage: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const detected = useDetectedTitles(organizationId, venueId);
  const titles = detected.data?.titles ?? [];
  // Nothing found (or not loaded): the section stays out of the way rather than showing an empty frame.
  if (titles.length === 0) return null;

  return (
    <Section title={t('library.detected.title')} description={t('library.detected.hint')} titleAs="h2" className="console-library-detected">
      <ul className="console-library-detected__list" aria-label={t('library.detected.title')}>
        {titles.map((title) => (
          <DetectedRow key={`${title.source}:${title.sourceId}`} title={title} organizationId={organizationId} venueId={venueId} canManage={canManage} />
        ))}
      </ul>
    </Section>
  );
}

function DetectedRow({
  title,
  organizationId,
  venueId,
  canManage,
}: {
  readonly title: Detected;
  readonly organizationId: string;
  readonly venueId: string;
  readonly canManage: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const add = useAddLibraryEntry(organizationId, venueId);
  const ask = useCreateCatalogueRequest(organizationId, venueId);
  const [requested, setRequested] = useState(false);
  const catalogueGameId = title.catalogueGameId;

  let action: ReactNode;
  if (title.inLibrary) {
    action = <span className="console-muted">{t('library.detected.inLibrary')}</span>;
  } else if (catalogueGameId === null && requested) {
    action = <Badge tone="ok">{t('library.detected.requested')}</Badge>;
  } else if (catalogueGameId === null && canManage) {
    action = (
      <Button
        size="sm"
        variant="secondary"
        busy={ask.isPending}
        label={t('library.detected.askHqLabel', { name: title.name })}
        onClick={() =>
          ask.mutate(
            { source: title.source, sourceId: title.sourceId, name: title.name },
            {
              onSuccess: () => {
                setRequested(true);
                show({ tone: 'ok', title: t('library.detected.asked', { name: title.name }) });
              },
              onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('library.detected.askFailed', { name: title.name }) }) }),
            },
          )
        }
      >
        <span>{t('library.detected.askHq')}</span>
      </Button>
    );
  } else if (catalogueGameId === null) {
    action = <span className="console-muted">{t('library.detected.notInCatalogue')}</span>;
  } else if (canManage) {
    action = (
      <Button
        size="sm"
        variant="secondary"
        busy={add.isPending}
        onClick={() =>
          add.mutate(
            { gameId: catalogueGameId, category: 'games' },
            {
              onSuccess: () => show({ tone: 'ok', title: t('library.detected.added', { name: title.name }) }),
              onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('library.detected.addFailed', { name: title.name }) }) }),
            },
          )
        }
      >
        {t('library.detected.add')}
      </Button>
    );
  } else {
    action = null;
  }

  return (
    <li className="console-library-detected__row">
      <div className="console-library-detected__name">
        <strong>{title.name}</strong>
        <span className="console-muted">
          {SOURCE_NAMES[title.source]}
          {title.versions.length > 0 ? ` · ${title.versions.join(', ')}` : ''}
        </span>
      </div>
      <Badge tone="neutral" title={title.stations.join(', ')}>
        {t('library.detected.onPcs', { count: title.stations.length })}
      </Badge>
      <div className="console-library-detected__action">{action}</div>
    </li>
  );
}
